import React, { useState, useEffect } from 'react';
import {
  Shield,
  CreditCard,
  Check,
  X,
  Loader2,
  AlertTriangle,
  Zap,
  Info,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import {
  OnboardingFormData,
  BusinessType,
  ReturnPolicyType,
  StripeConnectionStatus,
  TAX_ID_SENTINEL,
} from '../../../types/business';
import { cn } from '../../../lib/utils';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../../../config/firebase';
import { useAuth } from '../../../hooks/useAuth';

interface StepLegalProps {
  formData: OnboardingFormData;
  updateField: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  error: string | null;
}

const BUSINESS_TYPES: Array<{ id: BusinessType; label: string }> = [
  { id: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { id: 'llc', label: 'LLC' },
  { id: 'corporation', label: 'Corporation' },
  { id: 'partnership', label: 'Partnership' },
  { id: 'nonprofit', label: 'Non-Profit' },
];

const RETURN_POLICIES: Array<{
  id: ReturnPolicyType;
  label: string;
  description: string;
}> = [
  {
    id: 'full_refund',
    label: 'Full Refund',
    description: 'Accept returns for a full refund within the window',
  },
  {
    id: 'exchange_only',
    label: 'Exchange Only',
    description: 'Accept returns for exchange or store credit only',
  },
  {
    id: 'store_credit',
    label: 'Store Credit',
    description: 'Returns receive store credit only',
  },
  {
    id: 'no_returns',
    label: 'All Sales Final',
    description: 'No returns or exchanges accepted',
  },
  {
    id: 'custom',
    label: 'Custom Policy',
    description: 'Write your own return policy',
  },
];

const RETURN_WINDOWS = [7, 14, 30, 60, 90];

/**
 * Format EIN as XX-XXXXXXX while typing
 */
function formatEin(value: string): string {
  // Strip non-digits
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

export function StepLegal({ formData, updateField, error }: StepLegalProps) {
  const { user } = useAuth();
  const legal = formData.legal;

  // Stripe Connect state
  const [connecting, setConnecting] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeSuccess, setStripeSuccess] = useState<string | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectionStatus | null>(null);
  const [stripeApiKey, setStripeApiKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  // Stripe status lives with the other owner-only data
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(
      doc(db, 'businesses', user.uid, 'private', 'legal'),
      (snapshot) => {
        const data = snapshot.data();
        if (data?.stripe) {
          setStripeStatus(data.stripe as StripeConnectionStatus);
        }
      },
      (err) => {
        console.error('Error listening to stripe status:', err);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Handle legacy redirect from Stripe Connect OAuth (if any)
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.has('stripe_connect') || query.has('stripe_refresh')) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const updateLegal = (field: string, value: any) => {
    updateField('legal', { ...legal, [field]: value });
  };

  const updateReturnPolicy = (field: string, value: any) => {
    updateField('legal', {
      ...legal,
      returnPolicy: { ...legal.returnPolicy, [field]: value },
    });
  };

  // ── Stripe Key Connection ─────────────────────────────────────
  const handleConnectStripe = async () => {
    const key = stripeApiKey.trim();
    const pubKey = publishableKey.trim();
    if (!key || !pubKey) {
      setStripeError('Both keys are required');
      return;
    }
    if (!key.startsWith('rk_') && !key.startsWith('sk_')) {
      setStripeError('Secret/restricted key must start with rk_live_, rk_test_, sk_live_, or sk_test_');
      return;
    }
    if (!pubKey.startsWith('pk_')) {
      setStripeError('Publishable key must start with pk_test_ or pk_live_');
      return;
    }
    setConnecting(true);
    setStripeError(null);
    setStripeSuccess(null);

    try {
      const connectStripe = httpsCallable(functions, 'connectStripeAccount');
      const result = await connectStripe({ secretKey: key, publishableKey: pubKey });
      const data = result.data as { success: boolean; mode: string; accountName: string };
      setStripeSuccess(`Connected to "${data.accountName}" (${data.mode} mode)`);
      setStripeApiKey('');
      setPublishableKey('');
    } catch (err: any) {
      setStripeError(err?.message || 'Failed to connect Stripe.');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectStripe = async () => {
    setConnecting(true);
    setStripeError(null);
    setStripeSuccess(null);

    try {
      const disconnect = httpsCallable(functions, 'disconnectStripeConnect');
      await disconnect({});
      setStripeSuccess('Stripe disconnected successfully.');
      setStripeStatus(null);
    } catch (err: any) {
      setStripeError(err?.message || 'Failed to disconnect Stripe.');
    } finally {
      setConnecting(false);
    }
  };

  const isConnected = stripeStatus?.connected === true;
  const isOnboarded = stripeStatus?.onboardingComplete === true;
  const isChargesEnabled = stripeStatus?.chargesEnabled === true;
  const isPayoutsEnabled = stripeStatus?.payoutsEnabled === true;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif font-semibold text-heading">
          Legal & Payouts
        </h2>
        <p className="text-body mt-1">
          Business information for compliance and receiving payments.
        </p>
      </div>

      {/* Legal Business Name */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading">
          Legal Business Name <span className="text-error">*</span>
        </label>
        <p className="text-xs text-body">
          Your registered business name (may differ from your boutique name)
        </p>
        <div className="relative">
          <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder="e.g., Maison de Luxe LLC"
            value={legal.businessName}
            onChange={(e) => updateLegal('businessName', e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Business Type */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading">
          Business Type <span className="text-error">*</span>
        </label>
        <select
          value={legal.businessType}
          onChange={(e) => updateLegal('businessType', e.target.value)}
          className="flex h-12 w-full rounded-input border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans"
        >
          {BUSINESS_TYPES.map((type) => (
            <option key={type.id} value={type.id}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* EIN / Tax ID — Prominent */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading flex items-center gap-2">
          EIN / Tax ID <span className="text-error">*</span>
          <span className="relative group">
            <Info className="h-4 w-4 text-gray-400 cursor-help" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              Your Employer Identification Number (EIN). Required for tax reporting (1099-K) and to receive payouts. Format: XX-XXXXXXX
            </span>
          </span>
        </label>
        <p className="text-xs text-body">
          Required for tax reporting and payouts. You can find this on your IRS determination letter or{' '}
          <a href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            apply for one here
          </a>.
        </p>
        <div className="relative">
          <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder="XX-XXXXXXX"
            // A TIN already on file comes back as a sentinel, never the real
            // digits, so it is shown masked and cleared the moment you type.
            value={legal.taxId === TAX_ID_SENTINEL ? '••-•••••••' : legal.taxId}
            onFocus={() => {
              if (legal.taxId === TAX_ID_SENTINEL) updateLegal('taxId', '');
            }}
            onChange={(e) => updateLegal('taxId', formatEin(e.target.value))}
            maxLength={10}
            className="pl-10 font-mono tracking-wider"
          />
        </div>
        {legal.taxId === TAX_ID_SENTINEL ? (
          <p className="text-xs text-success flex items-center gap-1">
            <Check className="h-3 w-3" /> EIN on file, stored encrypted
          </p>
        ) : legal.taxId && legal.taxId.replace(/\D/g, '').length < 9 ? (
          <p className="text-xs text-warning">
            EIN must be 9 digits (XX-XXXXXXX)
          </p>
        ) : legal.taxId && legal.taxId.replace(/\D/g, '').length === 9 ? (
          <p className="text-xs text-success flex items-center gap-1">
            <Check className="h-3 w-3" /> Valid EIN format
          </p>
        ) : null}
      </div>

      {/* ── Stripe Connect ─────────────────────────────────── */}
      <div className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-purple-50/50 overflow-hidden">
        {/* Stripe Header */}
        <div className="px-5 py-4 bg-white/60 border-b border-primary/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center',
                isConnected && isOnboarded ? 'bg-green-100' : 'bg-primary/10'
              )}>
                {isConnected && isOnboarded ? (
                  <Check className="h-5 w-5 text-green-600" />
                ) : (
                  <CreditCard className="h-5 w-5 text-primary" />
                )}
              </div>
              <div>
                <h3 className="text-base font-semibold text-heading">
                  Payment Processing
                </h3>
                <p className="text-xs text-body">
                  Powered by Stripe Connect — secure payments & automatic payouts
                </p>
              </div>
            </div>
            {isConnected && isOnboarded && (
              <span className={cn(
                'px-2.5 py-1 rounded-full text-xs font-medium',
                stripeStatus?.mode === 'live'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              )}>
                {stripeStatus?.mode === 'live' ? 'Live' : 'Test Mode'}
              </span>
            )}
          </div>
        </div>

        <div className="p-5">
          {isConnected ? (
            /* ── Connected State ──────────────────────────────── */
            <div className="space-y-4">
              {/* Connected & Fully Onboarded */}
              {isOnboarded ? (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-200">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800">
                      Stripe Connected
                    </p>
                    <p className="text-xs text-green-600 mt-0.5">
                      {stripeStatus?.accountName || 'Your Stripe account'}
                      {stripeStatus?.lastFour && <> &middot; ****{stripeStatus.lastFour}</>}
                    </p>
                  </div>
                </div>
              ) : (
                /* Connected but onboarding incomplete */
                <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">
                      Stripe Setup Incomplete
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      Your account is linked but may need additional setup in your{' '}
                      <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                        Stripe Dashboard
                      </a>{' '}
                      before you can accept payments.
                    </p>
                  </div>
                </div>
              )}

              {/* Charges/Payouts status detail */}
              {isOnboarded && (!isChargesEnabled || !isPayoutsEnabled) && (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700">
                    <p className="font-medium">Account Restrictions</p>
                    <p className="mt-0.5">
                      {!isChargesEnabled && 'Charges are not yet enabled. '}
                      {!isPayoutsEnabled && 'Payouts are not yet enabled. '}
                      Visit your{' '}
                      <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                        Stripe Dashboard
                      </a>{' '}
                      to resolve.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/80 border border-gray-100">
                <Zap className="h-4 w-4 text-primary flex-shrink-0" />
                <p className="text-xs text-body">
                  Payments are automatically authorized at checkout and captured when
                  the courier picks up the order. Payouts are transferred directly to
                  your connected Stripe account.
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDisconnectStripe}
                disabled={connecting}
                className="text-gray-500 border-gray-200 hover:border-red-300 hover:text-red-500"
              >
                {connecting ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5 mr-1.5" />
                )}
                Disconnect Stripe
              </Button>
            </div>
          ) : (
            /* ── Not Connected ────────────────────────────────── */
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50/80 border border-blue-100">
                <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700 space-y-1">
                  <p className="font-medium">Connect your Stripe account to accept payments</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-blue-600">
                    <li>Go to your <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">Stripe Dashboard &rarr; API Keys</a></li>
                    <li>Copy your <strong>Publishable key</strong> (<code className="bg-blue-100 px-1 rounded">pk_live_...</code>)</li>
                    <li>Create a <strong>restricted key</strong> with <strong>Charges: Write</strong>, <strong>Refunds: Write</strong>, and <strong>Account: Read</strong></li>
                    <li>Paste both keys below</li>
                  </ol>
                  <p className="mt-1">
                    Don't have a Stripe account? <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener noreferrer" className="underline font-medium">Create one here</a> — it's free.
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-heading">Publishable Key</label>
                <Input
                  type="text"
                  placeholder="pk_live_... or pk_test_..."
                  value={publishableKey}
                  onChange={(e) => { setPublishableKey(e.target.value); setStripeError(null); }}
                  className="font-mono text-xs"
                  autoComplete="off"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-heading">Secret / Restricted Key</label>
                <div className="relative">
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    placeholder="rk_live_... or sk_live_..."
                    value={stripeApiKey}
                    onChange={(e) => { setStripeApiKey(e.target.value); setStripeError(null); }}
                    className="font-mono text-xs pr-10"
                    autoComplete="off"
                  />
                  <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-body">Restricted keys (rk_) are recommended for better security</p>
              </div>

              {stripeError && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{stripeError}</p>
                </div>
              )}
              {stripeSuccess && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-green-50 border border-green-200">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-700">{stripeSuccess}</p>
                </div>
              )}

              <Button
                type="button"
                onClick={handleConnectStripe}
                disabled={connecting || !stripeApiKey.trim() || !publishableKey.trim()}
                className="w-full"
              >
                {connecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Connect Stripe
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-body italic">
                You can also connect Stripe after launching — payouts will be held until connected.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Return Policy */}
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-heading block">
            Return Policy <span className="text-error">*</span>
          </label>
          <p className="text-xs text-body mt-0.5">
            Displayed to shoppers before purchase
          </p>
        </div>

        <div className="space-y-2">
          {RETURN_POLICIES.map((policy) => {
            const isSelected = legal.returnPolicy.type === policy.id;
            return (
              <button
                key={policy.id}
                type="button"
                onClick={() => updateReturnPolicy('type', policy.id)}
                className={cn(
                  'w-full flex items-center p-3 rounded-xl border-2 transition-all text-left',
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-100 hover:border-gray-200'
                )}
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-heading">{policy.label}</p>
                  <p className="text-xs text-body">{policy.description}</p>
                </div>
                <div
                  className={cn(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                    isSelected ? 'border-primary' : 'border-gray-300'
                  )}
                >
                  {isSelected && <div className="w-2.5 h-2.5 bg-primary rounded-full" />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Return Window */}
        {legal.returnPolicy.type !== 'no_returns' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-heading">Return Window</label>
            <div className="flex gap-2 flex-wrap">
              {RETURN_WINDOWS.map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => updateReturnPolicy('windowDays', days)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-medium border transition-all',
                    legal.returnPolicy.windowDays === days
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-body border-gray-200 hover:border-primary/40'
                  )}
                >
                  {days} days
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Custom policy description */}
        {legal.returnPolicy.type === 'custom' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-heading">
              Custom Policy Description <span className="text-error">*</span>
            </label>
            <textarea
              placeholder="Describe your return and exchange policy..."
              value={legal.returnPolicy.description}
              onChange={(e) => updateReturnPolicy('description', e.target.value)}
              rows={4}
              maxLength={500}
              className="flex w-full rounded-input border border-gray-200 bg-white px-3 py-3 text-sm ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans resize-none"
            />
          </div>
        )}
      </div>

    </div>
  );
}
